import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useQuery, useQueryClient } from "react-query"
import { fetchPickingTasksSummary, reassignPickingTask, fetchPickingStatus } from "../../services"
import { getOperatorUsers } from "../../services/userService"
import { Loader, Notification, SearchInput, Button, DialogView } from "../../components"
import { PickerQueueManager } from "./PickerQueueManager"
import { ClipboardListIcon } from "@heroicons/react/outline"
import { XIcon } from "@heroicons/react/solid"
import { PICK_TYPE_STYLES, STATUS_STYLES } from "../../utils/pickingStyles"
import { isTaskClosedShort, totalsForTask } from "./taskShortClosed"

export const PickingTasksSummary = ({ orderId, warehouseId, onClose }) => {
    const { i18n } = useTranslation()
    const queryClient = useQueryClient()

    const [reassignTask, setReassignTask] = useState(null)
    const [selectedPicker, setSelectedPicker] = useState(null)
    const [pickerSearch, setPickerSearch] = useState("")
    const [reassigning, setReassigning] = useState(false)
    const [notification, setNotification] = useState(null)
    const [queueManagerPicker, setQueueManagerPicker] = useState(null)

    const { data: tasks, isLoading, refetch } = useQuery(
        ["picking_tasks_summary", orderId],
        () => fetchPickingTasksSummary(orderId),
        { enabled: !!orderId, refetchOnWindowFocus: false }
    )

    // Get picking_order_id from the first task (if available) for picking-status endpoint
    const pickingOrderId = useMemo(() => {
        if (Array.isArray(tasks) && tasks.length > 0) return tasks[0].picking_order_id
        return null
    }, [tasks])

    const { data: pickingStatus } = useQuery(
        ["picking_status", pickingOrderId],
        () => fetchPickingStatus(pickingOrderId),
        { enabled: !!pickingOrderId, refetchOnWindowFocus: false }
    )

    // Scope the operator list to the ORDER's warehouse. Without warehouse_id the
    // /users/operators endpoint falls back to every warehouse the logged-in user
    // belongs to, which let a supervisor pick an operator from another warehouse —
    // the backend then rejects the reassign with a 401 (picker not in the order's
    // warehouse). See order 17233135 / CUBBO report.
    const { data: pickerData, isFetching: isFetchingPickers } = useQuery(
        ["operator_users_reassign", pickerSearch, warehouseId],
        () => getOperatorUsers({ search: pickerSearch, page: 1, per_page: 10, warehouse_id: warehouseId }),
        { enabled: !!reassignTask, keepPreviousData: true, refetchOnWindowFocus: false }
    )

    // Invalidate every cached subscription that depends on task ownership so
    // the per-picker progress widget and the picker-queue manager refresh
    // alongside the task list when an operator reassigns/unassigns work.
    const invalidateRelatedQueries = (affectedPickerIds = []) => {
        if (pickingOrderId) queryClient.invalidateQueries(["picking_status", pickingOrderId])
        affectedPickerIds.filter(Boolean).forEach(id => queryClient.invalidateQueries(["picker_queue", id]))
        queryClient.invalidateQueries(["processing_orders"])
    }

    const handleReassign = async (task, newPickerId) => {
        setReassigning(true)
        try {
            await reassignPickingTask(task.id, newPickerId)
            setReassignTask(null)
            setSelectedPicker(null)
            setPickerSearch("")
            setNotification({ type: "success", message: i18n.t("picking_tasks.actions.reassign_success") })
            refetch()
            // Both the previous owner's queue and the new owner's queue need to refresh.
            invalidateRelatedQueries([task.picker_id, newPickerId])
        } catch (error) {
            const msg = error.response?.data?.error?.message || error.response?.data?.error || i18n.t("picking_tasks.actions.reassign_error")
            setNotification({ type: "error", message: msg })
        }
        setReassigning(false)
    }

    const pickerProgress = useMemo(() => {
        // Prefer authoritative backend data if available
        if (pickingStatus?.pickers && Array.isArray(pickingStatus.pickers)) {
            return pickingStatus.pickers.map(p => ({
                picker_id: p.picker_id || p.id,
                picker_name: p.picker_name || (p.picker ? `${p.picker.first_name} ${p.picker.last_name}` : `Picker ${p.picker_id || p.id}`),
                total_tasks: p.total_tasks || 0,
                completed_tasks: p.completed_tasks || 0,
                expected_quantity: p.expected_quantity || 0,
                picked_quantity: p.picked_quantity || 0
            }))
        }
        // Fallback: compute from tasks
        if (!Array.isArray(tasks)) return []
        const map = new Map()
        for (const task of tasks) {
            if (!task.picker_id) continue
            if (!map.has(task.picker_id)) {
                map.set(task.picker_id, {
                    picker_id: task.picker_id,
                    picker_name: task.picker ? `${task.picker.first_name} ${task.picker.last_name}` : `Picker ${task.picker_id}`,
                    total_tasks: 0,
                    completed_tasks: 0,
                    expected_quantity: 0,
                    picked_quantity: 0
                })
            }
            const entry = map.get(task.picker_id)
            entry.total_tasks += 1
            if (task.status === "COMPLETED") entry.completed_tasks += 1
            // Deliberately the task counters, not the product breakdown: the backend resolves a
            // task's products from the whole location, so two tasks at one location report the
            // SAME units and adding them here would double the work and credit a picker who has
            // scanned nothing. The counters are per task, so each picker gets only their own.
            // A short close still shows its shortfall in the location row below.
            entry.expected_quantity += (task.expected_quantity || 0)
            entry.picked_quantity += (task.picked_quantity || 0)
        }
        return Array.from(map.values())
    }, [tasks, pickingStatus])

    // One row per PICKING TASK — the task is the unit of assignment (a task owns
    // a single picker_id), so the operator must see and act on tasks, not SKUs.
    //
    // A single PIECE task groups every loose stock unit at its location REGARDLESS
    // of product, so one task can span several products. Grouping the view by
    // product/SKU rendered that one task as several rows, and reassigning it (a
    // single, correct per-task call) changed every one of those rows at once.
    // Rendering one row per task removes that illusion: each row's products are
    // listed nested underneath, and reassigning/unassigning acts on the whole task.
    // (Boxes/pallets at the same location are already separate tasks, so they stay
    // as independent rows and remain individually reassignable.)
    const taskRows = useMemo(() => {
        if (!Array.isArray(tasks)) return []
        return tasks.map(task => {
            const products = (Array.isArray(task.products) ? task.products : []).map(p => ({
                key: p.product_id != null ? `pid:${p.product_id}` : (p.upc || (p.skus || []).join(",") || "—"),
                sku: (p.skus && p.skus.length > 0) ? p.skus.join(", ") : "—",
                name: p.name || null,
                upc: p.upc || null,
                expected: p.expected_quantity || 0,
                picked: p.picked_quantity || 0,
            }))
            const { expected, picked } = totalsForTask(task, products)
            return { task, products, expected, picked }
        })
    }, [tasks])

    if (isLoading) return <Loader show={true} />

    const taskList = Array.isArray(tasks) ? tasks : []

    if (taskList.length === 0) {
        return (
            <div className="p-6 text-center text-gray-500 relative">
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
                        aria-label={i18n.t("picking_tasks.multi_picker.close")}
                    >
                        <XIcon className="h-5 w-5" />
                    </button>
                )}
                {i18n.t("picking_tasks.empty")}
            </div>
        )
    }

    return (
        <div className="relative max-h-80vh overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white pb-3 border-b border-gray-100">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="text-lg font-medium text-gray-900">{i18n.t("picking_tasks.summary_title")}</h3>
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            aria-label={i18n.t("picking_tasks.multi_picker.close")}
                        >
                            <XIcon className="h-5 w-5" />
                        </button>
                    )}
                </div>
            </div>
            <div className="pt-4">

            {pickerProgress.length > 1 && (
                <div className="mb-4 p-3 bg-gray-50 rounded">
                    <div className="text-sm font-medium text-gray-700 mb-2">{i18n.t("picking_tasks.multi_picker.picking_status_title")}</div>
                    <div className="flex flex-wrap gap-3">
                        {pickerProgress.map((p) => {
                            const percent = p.expected_quantity === 0 ? 0 : Math.round((p.picked_quantity / p.expected_quantity) * 100)
                            return (
                                <div key={p.picker_id} className="flex-1 min-w-[200px] bg-white rounded p-2 border border-gray-200">
                                    <div className="flex justify-between items-start">
                                        <div className="text-sm font-medium text-gray-900">{p.picker_name}</div>
                                        <button
                                            className="text-indigo-600 hover:text-indigo-900"
                                            onClick={() => setQueueManagerPicker({ id: p.picker_id, name: p.picker_name })}
                                            title={i18n.t("picking_tasks.multi_picker.picker_queue")}
                                        >
                                            <ClipboardListIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <div className="text-xs text-gray-500 mb-1">{p.completed_tasks}/{p.total_tasks} {i18n.t("picking_tasks.multi_picker.tasks_short")}</div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full ${percent >= 100 ? "bg-green-500" : "bg-indigo-500"}`}
                                            style={{ width: `${Math.min(percent, 100)}%` }}
                                        />
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">{p.picked_quantity}/{p.expected_quantity} {i18n.t("picking_tasks.multi_picker.pieces_short")} ({percent}%)</div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            <div className="space-y-4">
                {taskRows.map(({ task, products, expected, picked }) => {
                    const percent = expected === 0 ? 0 : Math.round((picked / expected) * 100)
                    const canAct = ["PENDING", "ASSIGNED"].includes(task.status)
                    return (
                        <div key={task.id} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                                {products.map((p, i) => (
                                    <div key={p.key} className={i > 0 ? "mt-2 pt-2 border-t border-gray-200" : ""}>
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex items-baseline gap-2 flex-wrap">
                                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{i18n.t("picking_tasks.columns.sku")}</span>
                                                <span className="font-mono text-sm font-semibold text-gray-800">{p.sku}</span>
                                                {p.upc && (
                                                    <>
                                                        <span className="text-gray-300">·</span>
                                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{i18n.t("picking_tasks.multi_picker.upc")}</span>
                                                        <span className="font-mono text-xs text-gray-600">{p.upc}</span>
                                                    </>
                                                )}
                                            </div>
                                            {products.length > 1 && (
                                                <span className="text-xs text-gray-500 whitespace-nowrap">{p.picked}/{p.expected} {i18n.t("picking_tasks.multi_picker.pieces_short")}</span>
                                            )}
                                        </div>
                                        {p.name && <div className="mt-1 text-sm text-gray-700">{p.name}</div>}
                                    </div>
                                ))}
                            </div>
                            <table className="min-w-full">
                                <thead className="bg-white">
                                    <tr className="border-b border-gray-100">
                                        <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.location")}</th>
                                        <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.pick_type")}</th>
                                        <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.progress")}</th>
                                        <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.status")}</th>
                                        <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.picker")}</th>
                                        <th className="px-4 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    <tr>
                                        <td className="px-4 py-3 text-sm font-mono">{task.location_code}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PICK_TYPE_STYLES[task.pick_type] || "bg-gray-100 text-gray-800"}`}>
                                                {i18n.t(`picking_tasks.pick_types.${task.pick_type}`)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className={`h-2 rounded-full ${percent >= 100 ? "bg-green-500" : "bg-indigo-500"}`}
                                                        style={{ width: `${Math.min(percent, 100)}%` }}
                                                    />
                                                </div>
                                                <span className="text-sm text-gray-600 whitespace-nowrap">{picked}/{expected} ({percent}%)</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {isTaskClosedShort({ task, expected, picked }) ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                                    {i18n.t('picking_tasks.multi_picker.completed_short')}
                                                </span>
                                            ) : (
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[task.status] || "bg-gray-100 text-gray-800"}`}>
                                                    {i18n.t(`picking_tasks.statuses.${task.status}`)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            {task.picker ? `${task.picker.first_name} ${task.picker.last_name}` : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {canAct && (
                                                <button
                                                    className="text-indigo-600 hover:text-indigo-900 text-sm font-medium hover:underline"
                                                    onClick={() => { setReassignTask(task); setSelectedPicker(null); setPickerSearch("") }}
                                                >
                                                    {i18n.t("picking_tasks.actions.reassign_picker")}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )
                })}
            </div>
            </div>

            <DialogView open={!!reassignTask} setOpen={() => { setReassignTask(null); setSelectedPicker(null) }} className="justify-items-start">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                    {i18n.t("picking_tasks.actions.reassign_picker_title")}
                    {reassignTask && <span className="text-sm text-gray-500 ml-2">— {reassignTask.location_code}</span>}
                </h3>
                <SearchInput
                    onChange={(v) => setPickerSearch(v)}
                    debounce={250}
                    placeholder={i18n.t("picking_tasks.actions.search_operator_placeholder")}
                    className="w-full mb-3"
                />
                <div className="max-h-60 overflow-y-auto w-full">
                    {pickerData?.users?.map((picker) => {
                        const isSelected = selectedPicker?.id === picker.id
                        return (
                            <button
                                key={picker.id}
                                className={`w-full text-left px-3 py-2 rounded flex justify-between items-center ${isSelected ? "bg-indigo-50 ring-1 ring-indigo-500" : "hover:bg-gray-100"}`}
                                onClick={() => setSelectedPicker(picker)}
                            >
                                <span className="min-w-0">
                                    <span className="block truncate">{picker.first_name} {picker.last_name}</span>
                                    {picker.email && (
                                        <span className="block text-xs text-gray-500 truncate" title={picker.email}>{picker.email}</span>
                                    )}
                                </span>
                                {reassignTask?.picker_id === picker.id && <span className="text-xs text-gray-400 ml-2 shrink-0">({i18n.t("picking_tasks.actions.current")})</span>}
                            </button>
                        )
                    })}
                </div>
                <div className="mt-3 flex justify-end gap-2">
                    <Button type="secondary" onClick={() => { setReassignTask(null); setSelectedPicker(null) }}>{i18n.t("common.cancel")}</Button>
                    <Button
                        onClick={() => handleReassign(reassignTask, selectedPicker.id)}
                        disabled={!selectedPicker || reassigning || isFetchingPickers}
                    >
                        {i18n.t("picking_tasks.actions.reassign_confirm")}
                    </Button>
                </div>
            </DialogView>

            <Notification
                show={!!notification}
                setShow={() => setNotification(null)}
                type={notification?.type || "success"}
                title={notification?.message || ""}
            />

            <DialogView
                open={!!queueManagerPicker}
                setOpen={() => setQueueManagerPicker(null)}
                closeOnBackdrop
                className="justify-items-start sm:max-w-2xl"
            >
                {queueManagerPicker && (
                    <PickerQueueManager
                        pickerId={queueManagerPicker.id}
                        pickerName={queueManagerPicker.name}
                        onClose={() => setQueueManagerPicker(null)}
                    />
                )}
            </DialogView>
        </div>
    )
}
